import { format } from 'date-fns';
import { useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Box, EmptyState, Text, VStack } from '../../../components/primitives';
import { type TranslationParams, useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import { buildAgendaSections } from '../utils/calendarAgendaSections';
import {
  getViewerTimeZone,
  isToday,
  type LocalDate,
  localDateKey,
  toNativeDate,
} from '../utils/calendarDate';
import { CalendarAgendaRow } from './CalendarAgendaRow';

interface CalendarAgendaListProps {
  /** The first day of the rolling range — see AGENDA_RANGE_DAYS. */
  anchor: LocalDate;
  events: CalendarEvent[];
  timeZone?: string;
  /** Injectable for tests; defaults to the real current instant. */
  now?: Date;
  onEventPress?: (event: CalendarEvent) => void;
}

/**
 * The forward-looking list: what is coming up, day by day.
 *
 * This is the view that flows — no fixed-height rows, nothing positioned by
 * duration — so it is also the one that survives a large font scale, where the
 * hour grid clips. Days with nothing on them are omitted rather than rendered
 * empty; that dead space is exactly what the 24-hour grid does badly.
 */
export function CalendarAgendaList({
  anchor,
  events,
  timeZone = getViewerTimeZone(),
  now = new Date(),
  onEventPress,
}: Readonly<CalendarAgendaListProps>) {
  const { t } = useTranslation();

  const sections = useMemo(() => buildAgendaSections(events, timeZone), [events, timeZone]);

  // Unlike Month/Week/Day, an empty agenda really is empty: there is no grid
  // standing in for the period, so a blank screen would read as a failed load.
  if (sections.length === 0) {
    return (
      <Box testID="calendar_agenda_empty" flex={1} alignItems="center" justifyContent="center">
        <EmptyState
          title={t('calendar.agenda.emptyTitle')}
          description={t('calendar.agenda.emptyDescription')}
        />
      </Box>
    );
  }

  return (
    <ScrollView
      testID="calendar_agenda_list"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.content}
    >
      <VStack spacing="xl">
        {sections.map((section) => (
          <VStack
            key={localDateKey(section.date)}
            testID={`calendar_agenda_section_${localDateKey(section.date)}`}
            spacing="sm"
          >
            <Box padding="xxs">
              <Text
                variant="bodySmall"
                accessibilityRole="header"
                color={
                  isToday(section.date, timeZone, now) ? colors.primary[500] : colors.text.primary
                }
                style={styles.dayTitle}
              >
                {isToday(section.date, timeZone, now)
                  ? t('calendar.agenda.today')
                  : format(toNativeDate(section.date), 'EEE, d MMM')}
              </Text>
              <Text variant="caption" color={colors.text.secondary}>
                {summarise(section.meetingCount, section.cancelledCount, t)}
              </Text>
            </Box>

            <VStack spacing="sm">
              {section.events.map((event) => (
                <CalendarAgendaRow key={event.id} event={event} onPress={onEventPress} />
              ))}
            </VStack>
          </VStack>
        ))}
      </VStack>
    </ScrollView>
  );
}

function summarise(
  meetingCount: number,
  cancelledCount: number,
  t: (key: string, params?: TranslationParams) => string,
): string {
  const parts = [t('calendar.agenda.meetingCount', { count: meetingCount })];
  if (cancelledCount > 0) {
    parts.push(t('calendar.agenda.cancelledCount', { count: cancelledCount }));
  }
  return parts.join(' · ');
}

const styles = StyleSheet.create({
  content: {
    padding: tokens.spacing.lg,
  },
  dayTitle: {
    fontWeight: '600',
  },
});
